import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { Thumb } from "@/components/VendorCatalog";
import { Printer, ArrowRight, X, Lightning } from "@phosphor-icons/react";

const money = (n) => `${(Number(n) || 0).toFixed(2)} $`;
const ago = (iso, fr) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return fr ? "à l'instant" : "just now";
  if (m < 60) return fr ? `il y a ${m} min` : `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return fr ? `il y a ${h} h` : `${h} h ago`;
  return new Date(iso).toLocaleDateString(fr ? "fr-CA" : "en-CA");
};

function DraftModal({ id, fr, onClose, onConverted }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/vendor-drafts/${id}`).then((r) => setD(r.data)).catch(() => { toast.error(fr ? "Brouillon introuvable" : "Draft not found"); onClose(); }); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const convert = async () => {
    if (!window.confirm(fr ? "Transformer ce brouillon en commande ? Il disparaîtra de la zone du vendeur." : "Convert this draft into an order? It will disappear from the vendor's area.")) return;
    setBusy(true);
    try { const { data } = await axios.post(`${API}/admin/vendor-drafts/${id}/convert`); onConverted(data.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); setBusy(false); }
  };
  const c = d?.customer || {};
  return (
    <div data-testid="draft-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="font-display text-lg font-bold">{fr ? "Commande en préparation" : "Order in preparation"}</div>
          <button data-testid="draft-modal-close" onClick={onClose}><X size={20} weight="bold" /></button>
        </div>
        {!d ? <div className="p-10 text-center text-sm text-muted-foreground">…</div> : (
          <>
            <div className="overflow-y-auto px-5 py-4">
              <div className="mb-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{fr ? "Client" : "Client"}</div>
                  <div className="font-bold">{c.succursale || c.business_name || "—"}</div>
                  <div className="text-sm">{[c.address, [c.city, c.province, c.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>
                  <div className="font-mono text-xs text-muted-foreground">{[c.coop_code && `COOP ${c.coop_code}`, c.account_number && `#${c.account_number}`].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="text-sm sm:text-right">
                  <div><span className="text-muted-foreground">{fr ? "Vendeur" : "Vendor"} : </span><b>{d.vendor_name}</b></div>
                  {d.orderer_name && <div><span className="text-muted-foreground">{fr ? "Commandé par" : "Ordered by"} : </span>{d.orderer_name}</div>}
                  <div><span className="text-muted-foreground">{fr ? "Étape" : "Step"} : </span>{d.step}/3 · {ago(d.updated_at, fr)}</div>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="py-2"></th><th>{fr ? "Produit" : "Product"}</th><th className="text-right">{fr ? "Qté" : "Qty"}</th><th className="text-right">{fr ? "Dernier prix" : "Last price"}</th><th className="text-right">Total</th>
                </tr></thead>
                <tbody>
                  {d.items.map((it, i) => (
                    <tr key={i} data-testid={`draft-item-${i}`} className="border-b border-border">
                      <td className="py-2 pr-2"><Thumb src={it.image} className="h-10 w-10 rounded border border-border p-0.5" /></td>
                      <td><div className="font-medium">{it.name}</div><div className="font-mono text-[11px] text-primary">{it.product_code}{it.variant_value ? ` · ${it.variant_value}` : ""}</div></td>
                      <td className="text-right">{it.qty}</td>
                      <td className="text-right">{it.unit_price ? money(it.unit_price) : <span className="text-amber-600">—</span>}</td>
                      <td className="text-right font-semibold">{money(it.unit_price * it.qty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div data-testid="draft-estimated-total" className="mt-3 text-right text-sm">{fr ? "Sous-total estimé (avant taxes)" : "Estimated subtotal (before tax)"} : <b>{money(d.estimated_total)}</b></div>
            </div>
            <div className="flex flex-wrap justify-end gap-3 border-t border-border px-5 py-3">
              <a data-testid="draft-print" href={`${API}/admin/vendor-drafts/${id}/pdf`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs font-bold uppercase tracking-wider"><Printer size={14} weight="bold" /> {fr ? "Imprimer" : "Print"}</a>
              <button data-testid="draft-convert" disabled={busy || !c.id || !d.items.length} onClick={convert}
                className="inline-flex items-center gap-2 bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
                {fr ? "Compléter, confirmer et facturer" : "Complete, confirm & invoice"} <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function VendorLivePage() {
  const { lang } = useLang();
  const fr = lang !== "en";
  const navigate = useNavigate();
  const [data, setData] = useState({ orders: [], drafts: [], vendors: [] });
  const [tab, setTab] = useState("orders");
  const [vendor, setVendor] = useState("");
  const [openDraft, setOpenDraft] = useState(null);
  const [, tick] = useState(0);

  const load = () => axios.get(`${API}/admin/vendor-live`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const iv = setInterval(() => { load(); tick((x) => x + 1); }, 15000);
    return () => clearInterval(iv);
  }, []);

  const orders = useMemo(() => data.orders.filter((o) => !vendor || o.vendor_email === vendor), [data, vendor]);
  const drafts = useMemo(() => data.drafts.filter((o) => !vendor || o.vendor_email === vendor), [data, vendor]);
  const TabBtn = ({ k, label, n }) => (
    <button data-testid={`live-tab-${k}`} onClick={() => setTab(k)}
      className={`border-b-2 px-4 py-3 text-xs font-bold uppercase tracking-wider ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
      {label} <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] ${tab === k ? "bg-primary text-white" : "bg-muted"}`}>{n}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main data-testid="vendor-live-page" className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 font-display text-3xl font-bold"><Lightning size={28} weight="fill" className="text-primary" /> {fr ? "Vendeurs en direct" : "Live vendors"}</h1>
            <p className="text-sm text-muted-foreground">{fr ? "Mise à jour automatique toutes les 15 secondes" : "Auto-refresh every 15 seconds"}</p>
          </div>
          <select data-testid="live-vendor-filter" value={vendor} onChange={(e) => setVendor(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm">
            <option value="">{fr ? "Tous les vendeurs" : "All vendors"}</option>
            {data.vendors.map((v) => <option key={v.email} value={v.email}>{v.name}</option>)}
          </select>
        </div>
        <div className="mb-4 flex border-b border-border">
          <TabBtn k="orders" label={fr ? "Nouvelles commandes" : "New orders"} n={orders.length} />
          <TabBtn k="drafts" label={fr ? "En préparation" : "In preparation"} n={drafts.length} />
        </div>
        {tab === "orders" ? (
          <div className="overflow-x-auto border border-border bg-card">
            <table className="w-full min-w-[760px] text-sm">
              <thead><tr className="border-b border-border bg-muted/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2">{fr ? "Reçue" : "Received"}</th><th>{fr ? "Vendeur" : "Vendor"}</th><th>Client</th><th className="text-right">{fr ? "Produits" : "Items"}</th><th className="text-right">Total</th><th>{fr ? "Signée" : "Signed"}</th><th>{fr ? "Bon par courriel" : "PO email"}</th>
              </tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`live-order-${o.id}`} onClick={() => navigate(`/admin/orders/${o.id}`)} className="cursor-pointer border-b border-border hover:bg-muted/40">
                    <td className="px-3 py-2 whitespace-nowrap">{ago(o.created_at, fr)}</td>
                    <td>{o.vendor_name}</td>
                    <td><div className="font-medium">{o.business_name}</div><div className="text-xs text-muted-foreground">{o.city}</div></td>
                    <td className="text-right">{o.items_count}</td>
                    <td className="text-right font-semibold">{o.grand_total ? money(o.grand_total) : <span className="text-amber-600">{fr ? "à chiffrer" : "to price"}</span>}</td>
                    <td>{o.signed ? "✓" : "—"}</td>
                    <td className="text-xs">{o.po_email_status === "sent" ? (fr ? "envoyé" : "sent") : o.po_email_status === "failed" ? <span className="text-red-600">{fr ? "échec" : "failed"}</span> : "—"}</td>
                  </tr>
                ))}
                {!orders.length && <tr><td colSpan={7} className="px-3 py-10 text-center text-muted-foreground">{fr ? "Aucune nouvelle commande de vendeur" : "No new vendor orders"}</td></tr>}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto border border-border bg-card">
            <table className="w-full min-w-[700px] text-sm">
              <thead><tr className="border-b border-border bg-muted/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2">{fr ? "Modifiée" : "Edited"}</th><th>{fr ? "Vendeur" : "Vendor"}</th><th>Client</th><th className="text-right">{fr ? "Produits" : "Items"}</th><th className="text-center">{fr ? "Étape" : "Step"}</th>
              </tr></thead>
              <tbody>
                {drafts.map((d) => (
                  <tr key={d.id} data-testid={`live-draft-${d.id}`} onClick={() => setOpenDraft(d.id)} className="cursor-pointer border-b border-border hover:bg-muted/40">
                    <td className="px-3 py-2 whitespace-nowrap">{ago(d.updated_at, fr)}</td>
                    <td>{d.vendor_name}</td>
                    <td><div className="font-medium">{d.business_name || (fr ? "Client non choisi" : "No client")}</div><div className="text-xs text-muted-foreground">{d.city}</div></td>
                    <td className="text-right">{d.items_count}</td>
                    <td className="text-center">{d.step}/3</td>
                  </tr>
                ))}
                {!drafts.length && <tr><td colSpan={5} className="px-3 py-10 text-center text-muted-foreground">{fr ? "Aucune commande en préparation" : "No orders in preparation"}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </main>
      {openDraft && <DraftModal id={openDraft} fr={fr} onClose={() => setOpenDraft(null)} onConverted={(oid) => navigate(`/admin/orders/${oid}`)} />}
    </div>
  );
}
